import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {  Card, message } from "antd";
import ParsedQuestionsTable from "./ParsedQuestionsTable";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import { useBulkUploadQuiz } from "../../../hooks/managequiz/useBulkUploadQuiz";
import CustomButton from "../../../components/global/Button/CustomButton";

function BulkReviewPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const initialData = location.state?.parsedQuestions || [];
  const quizId = location.state?.quizId;

  const [questions, setQuestions] = useState(initialData);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasApiErrors, setHasApiErrors] = useState(false);

  const { addBulkQuizQuestions, bulkLoading } = useBulkUploadQuiz(quizId);

  const stats = {
    total: questions.length,
    valid: hasApiErrors
      ? questions.filter((_, idx) => !validationErrors[idx + 1]?.length).length
      : questions.length,
    invalid: hasApiErrors
      ? Object.values(validationErrors).filter((errs) => errs && errs.length > 0).length
      : 0,
  };

  const sanitizePayload = (rawQuestions) => {
    return rawQuestions.map((q) => ({
      questionType: q.questionType || "MCQ_SINGLE",
      questionText: q.questionText || "",
      points: String(q.points ?? "1"),
      option1: q.option1 || "",
      option2: q.option2 || "",
      option3: q.option3 || "",
      option4: q.option4 || "",
      option5: q.option5 || "",
      correctAnswer: Array.isArray(q.correctAnswer)
        ? q.correctAnswer.join(",")
        : String(q.correctAnswer || ""),
    }));
  };

  const handleConfirmImport = async () => {
    if (!quizId) {
      message.error("Quiz ID is missing.");
      return;
    }

    if (questions.length === 0) {
      message.warning("Please add at least one question before importing.");
      return;
    }

    const payload = sanitizePayload(questions);

    try {
      const response = await addBulkQuizQuestions(payload);

      if (response?.status === "SUCCESS") {
        message.success("Bulk upload completed successfully!");
        setHasApiErrors(false);
        setValidationErrors({});
        navigate(-1)
      } else if (response?.status === "ERROR") {
        message.error(response?.message || "Bulk upload validation failed");

        const records = response?.data?.records || [];
        const errorMap = {};

        records.forEach((rec, index) => {
          const rowKey = rec.rowNumber || index + 1;
          if (rec.errors && rec.errors.length > 0) {
            errorMap[rowKey] = rec.errors;
          }
        });

        setValidationErrors(errorMap);
        setHasApiErrors(true);
      }
    } catch (err) {
      message.error(err?.message || "Failed to submit questions.");
    }
  };


  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="p-6 bg-[#F9F9F9] min-h-screen rounded-lg" >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#910E11] mt-1">Review & Import Questions</h1>
          <p className="text-sm text-gray-500">
            Verify questions, fix validation errors returned by server, and confirm import into your quiz.
          </p>
        </div>
        <div className="flex gap-3">
          <CustomButton type="default" variant="outlined" onClick={handleCancel}>Cancel & Exit</CustomButton>
          <CustomButton
            type="primary"
            className="bg-red-700 hover:bg-red-800"
            disabled={stats.total === 0}
            loading={bulkLoading}
            onClick={handleConfirmImport}
          >
            Confirm & Import ({stats.total} items)
          </CustomButton>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="shadow-sm border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
            <span className="text-xs font-semibold text-gray-500 uppercase">Total Questions</span>
          </div>
          <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
          <p className="text-xs text-gray-400 mt-1">Items staged for upload</p>
        </Card>

        <Card className="shadow-sm border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
            <span className="text-xs font-semibold text-gray-500 uppercase">
              {hasApiErrors ? "Valid Records" : "Ready to Import"}
            </span>
          </div>
          <div className="text-3xl font-bold text-gray-800">{stats.valid}</div>
          <p className="text-xs text-gray-400 mt-1">
            {hasApiErrors ? "Passed server validation" : "Pending API submission"}
          </p>
        </Card>

        <Card className="shadow-sm border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
            <span className="text-xs font-semibold text-gray-500 uppercase">Validation Errors</span>
          </div>
          <div className="text-3xl font-bold text-gray-800">{stats.invalid}</div>
          <p className="text-xs text-gray-400 mt-1">
            {hasApiErrors ? "Requires correction before successful import" : "0 errors before submission"}
          </p>
        </Card>
      </div>

        <ParsedQuestionsTable
          questions={questions}
          setQuestions={setQuestions}
          onSubmit={handleConfirmImport}
          onCancel={handleCancel}
          validationErrors={validationErrors}
          bulkLoading={bulkLoading}
          hasApiErrors={hasApiErrors}
        />
    </div>
  );
}

export default AdminHOC(BulkReviewPage);